import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import ImageFrame from "@/components/site/ImageFrame";
import Reveal from "@/components/site/Reveal";
import Seo from "@/components/site/Seo";
import PageHeader from "@/components/site/PageHeader";

function Project({ label, title, text, note, image, alt, reverse }) {
  return (
    <div className="grid gap-12 lg:grid-cols-12 items-center">
      <Reveal className={`lg:col-span-5 ${reverse ? "lg:order-2 lg:col-start-8" : ""}`}>
        <ImageFrame src={image} label="BORGO PHOTO" alt={alt} ratio="4 / 5" />
      </Reveal>
      <div className={`lg:col-span-6 ${reverse ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
        <span className="inline-block border border-sage/50 px-4 py-2 eyebrow text-sage">{label}</span>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-[3.2rem] leading-[1.07] text-wood max-w-[22ch]">
          {title}
        </h2>
        <Reveal delay={0.1} className="mt-7 prose-ed text-sm md:text-lg text-foreground/78 max-w-xl">
          {(text || "").split("\n\n").map((p) => (
            <p key={p.slice(0, 60)}>{p}</p>
          ))}
        </Reveal>
        <p className="mt-6 text-xs tracking-wider text-foreground/60">{note}</p>
      </div>
    </div>
  );
}

export default function ProgettiFuturi() {
  const { settings } = useSettings();
  return (
    <>
      <Seo
        title="Progetti futuri · Osteria Aterrana e il borgo di Aterrana"
        description="I progetti in sviluppo di Osteria Aterrana: l'Orto dell'Osteria e l'idea di un albergo diffuso nelle case del borgo di Aterrana, a Montoro. Progetti non ancora attivi."
        path="/progetti-futuri"
      />
      <PageHeader
        eyebrow="Progetti futuri"
        title="L'osteria è il primo passo. Il progetto guarda al borgo."
        lead={settings.progetto_intro}
        label="BORGO PHOTO"
        alt="Il borgo storico di Aterrana"
        src={settings.borgo_header_image}
      />

      <section className="section-y bg-cream" data-testid="progetti-futuri">
        <div className="container-ed space-y-24 md:space-y-32">
          <Project
            label={settings.orto_label || "Progetto futuro"}
            title={settings.orto_name || "Orto dell'Osteria"}
            text={settings.orto_text}
            note={`L'${settings.orto_name || "Orto dell'Osteria"} non è ancora attivo: quello che serviamo oggi arriva da produttori scelti, non dal nostro orto.`}
            image={settings.territory_image_2}
            alt="Gerani su un davanzale in pietra ad Aterrana"
          />
          <Project
            reverse
            label={settings.albergo_label || "La visione"}
            title="Albergo diffuso di Aterrana"
            text={settings.albergo_text}
            note={settings.albergo_note || "Progetto futuro: l'albergo diffuso non esiste ancora e non è prenotabile."}
            image={settings.experience_image_2}
            alt="Una finestra del borgo di Aterrana"
          />
        </div>
      </section>

      <section className="bg-wood-deep grain text-cream py-24 md:py-28" data-testid="progetti-futuri-chiusura">
        <div className="container-ed max-w-3xl">
          <p className="eyebrow text-gold">Un passo alla volta</p>
          <p className="mt-7 font-serif text-2xl sm:text-3xl lg:text-[2.4rem] leading-[1.25]">
            Oggi l'osteria. Domani l'orto. Poi, se il borgo ci segue, l'ospitalità dentro le sue case.
          </p>
          <p className="mt-8 text-sm md:text-lg text-cream/70 leading-relaxed">{settings.progetto_closing}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link
              to="/prenota"
              data-testid="progetti-book"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-wood-deep text-[0.7rem] uppercase tracking-[0.2em] hover:bg-cream transition-colors duration-500"
            >
              Prenota un tavolo <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
            <a
              href={`tel:${settings.phone_link || "+393311091971"}`}
              className="inline-flex items-center gap-2 px-8 py-4 border border-cream/35 text-cream text-[0.7rem] uppercase tracking-[0.2em] hover:bg-cream hover:text-wood transition-colors duration-500"
            >
              <Phone size={14} /> {settings.phone || "331 10 91 971"}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
